import { FiPlus, FiTrash2, FiClipboard } from "react-icons/fi";
import { Prescription, ExerciseInPlan } from "../../../../types";
import { ExerciseRow } from "./ExerciseRow";

interface PrescriptionSectionProps {
  prescriptions: Prescription[];
  professionalId: string;
  onAddPrescription: () => void;
  onRemovePrescription: (index: number) => void;
  onUpdatePrescriptionName: (index: number, newName: string) => void;
  onAddExercise: (prescIndex: number) => void;
  onUpdateExercise: (
    prescIndex: number,
    exerIndex: number,
    field: keyof ExerciseInPlan,
    value: string | number
  ) => void;
  onRemoveExercise: (prescIndex: number, exerIndex: number) => void;
  prescriptionsEndRef: React.RefObject<HTMLDivElement>;
}

export const PrescriptionSection = ({
  prescriptions,
  professionalId,
  onAddPrescription,
  onRemovePrescription,
  onUpdatePrescriptionName,
  onAddExercise,
  onUpdateExercise,
  onRemoveExercise,
  prescriptionsEndRef,
}: PrescriptionSectionProps) => {
  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-5 min-w-0 overflow-x-hidden">
      {/* Header - Mobile First */}
      <div className="space-y-3 md:space-y-0 md:flex md:items-center md:justify-between min-w-0">
        <div>
          <h4 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 dark:text-white">
            Treinos do Plano
          </h4>
          <p className="mt-0.5 sm:mt-1 text-[11px] sm:text-xs md:text-sm text-gray-600 dark:text-gray-400">
            Organize os treinos por letras (A, B, C...) ou dias da semana
          </p>
        </div>
        <button
          type="button"
          onClick={onAddPrescription}
          className="w-full md:w-auto inline-flex items-center justify-center gap-2 rounded-lg sm:rounded-xl bg-gradient-to-r from-purple-600 to-purple-600 px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 text-sm font-semibold text-white shadow-sm transition-all active:scale-95 md:hover:scale-105 md:hover:shadow-xl touch-manipulation"
        >
          <FiPlus size={16} className="sm:w-[18px] sm:h-[18px]" />
          Adicionar Treino
        </button>
      </div>

      {/* Prescription Cards - Mobile First */}
      {prescriptions.map((presc, pIndex) => (
        <div
          key={`prescription-${presc.name}-${pIndex}`}
          className="group relative overflow-x-hidden overflow-y-visible rounded-lg sm:rounded-xl md:rounded-2xl border-2 border-stroke bg-white p-3 sm:p-4 md:p-6 shadow-sm transition-all dark:border-strokedark dark:bg-boxdark min-w-0"
        >
          {/* Header do Treino - Mobile First */}
          <div className="relative mb-3 sm:mb-4 md:mb-6 space-y-2.5 sm:space-y-3 md:space-y-0 md:flex md:items-center md:gap-3">
            {/* Badge e Delete Button Row - Mobile First */}
            <div className="flex items-center justify-between gap-2 sm:gap-3">
              <div className="flex h-9 w-9 sm:h-10 sm:w-10 md:h-12 md:w-12 shrink-0 items-center justify-center rounded-md sm:rounded-lg md:rounded-xl bg-gradient-to-br from-purple-600 to-purple-600 text-base sm:text-lg md:text-xl font-bold text-white shadow-sm">
                {String.fromCodePoint(65 + pIndex)}
              </div>
              <button
                type="button"
                onClick={() => onRemovePrescription(pIndex)}
                className="md:hidden flex items-center justify-center rounded-md sm:rounded-lg border-2 border-red-200 p-2 sm:p-2.5 text-red-600 transition-all active:scale-95 active:bg-red-50 dark:border-red-800 dark:text-red-400 dark:active:bg-red-900/20 touch-manipulation"
                title="Remover treino"
              >
                <FiTrash2 size={16} className="sm:w-[18px] sm:h-[18px]" />
              </button>
            </div>

            {/* Input Field - Mobile First */}
            <input
              type="text"
              value={presc.name}
              onChange={(e) => onUpdatePrescriptionName(pIndex, e.target.value)}
              className="w-full md:flex-1 box-border max-w-full rounded-md sm:rounded-lg md:rounded-xl border border-stroke bg-gray-50 px-3 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 text-sm md:text-base font-bold outline-none transition focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/20 dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary touch-manipulation min-w-0"
              placeholder="Nome do Treino (ex: Treino A)"
            />

            {/* Delete Button - Desktop Only */}
            <button
              type="button"
              onClick={() => onRemovePrescription(pIndex)}
              className="hidden md:flex md:items-center md:justify-center rounded-xl border-2 border-red-200 p-3 text-red-600 transition-all hover:bg-red-50 hover:border-red-300 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
              title="Remover treino"
            >
              <FiTrash2 size={20} />
            </button>
          </div>

          {/* Exercícios - Mobile First */}
          <div className="space-y-2.5 sm:space-y-3 md:space-y-4">
            {/* Header Labels - Hidden on Mobile, Show on Desktop */}
            {presc.exercises.length > 0 && (
              <div className="hidden md:grid mb-3 grid-cols-12 gap-3 px-1 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 min-w-0 overflow-x-hidden">
                <div className="col-span-3">Exercício</div>
                <div className="col-span-2 text-center">Séries</div>
                <div className="col-span-2 text-center">Reps</div>
                <div className="col-span-2 text-center">Carga</div>
                <div className="col-span-1 text-center">Int.</div>
                <div className="col-span-1 text-center">Imagem</div>
                <div className="col-span-1"></div>
              </div>
            )}

            {presc.exercises.map((ex, eIndex) => (
              <ExerciseRow
                key={`exercise-${ex.id || ex.name}-${eIndex}`}
                exercise={ex}
                prescIndex={pIndex}
                exerIndex={eIndex}
                professionalId={professionalId}
                onUpdate={(field, value) =>
                  onUpdateExercise(pIndex, eIndex, field, value)
                }
                onRemove={() => onRemoveExercise(pIndex, eIndex)}
              />
            ))}

            {/* Botão Adicionar Exercício - Mobile First */}
            <button
              type="button"
              onClick={() => onAddExercise(pIndex)}
              className="flex w-full items-center justify-center gap-2 rounded-md sm:rounded-lg md:rounded-xl border-2 border-dashed border-purple-300 bg-purple-50/50 py-2.5 sm:py-3 md:py-4 text-xs sm:text-sm font-semibold text-purple-600 transition-all active:scale-[0.98] active:border-purple-400 active:bg-purple-50 md:hover:border-purple-400 md:hover:bg-purple-50 dark:border-purple-800 dark:bg-purple-900/10 dark:text-purple-400 dark:active:bg-purple-900/20 md:dark:hover:bg-purple-900/20 touch-manipulation"
            >
              <FiPlus size={14} className="sm:w-4 sm:h-4" /> Adicionar Exercício
            </button>
          </div>
        </div>
      ))}

      {/* Ref para scroll automático */}
      <div ref={prescriptionsEndRef} />

      {/* Estado Vazio - Mobile First */}
      {prescriptions.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg sm:rounded-xl md:rounded-2xl border-2 border-dashed border-gray-300 bg-white py-10 sm:py-12 md:py-16 px-4 dark:border-gray-700 dark:bg-boxdark">
          <div className="rounded-full bg-gradient-to-br from-purple-100 to-purple-100 p-4 sm:p-5 md:p-6 dark:from-purple-900/30 dark:to-purple-900/30">
            <FiClipboard
              size={32}
              className="sm:w-10 sm:h-10 md:w-12 md:h-12 text-gray-400 dark:text-gray-600"
            />
          </div>
          <p className="mt-3 sm:mt-4 md:mt-5 text-sm sm:text-base md:text-lg font-semibold text-gray-700 dark:text-gray-300 text-center">
            Nenhum treino adicionado
          </p>
          <p className="mt-1.5 sm:mt-2 text-[11px] sm:text-xs md:text-sm text-gray-500 dark:text-gray-500 text-center px-4">
            Clique em "Adicionar Treino" para começar a criar o plano
          </p>
        </div>
      )}
    </div>
  );
};
